import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowRight,
  Bot,
  CalendarCheck,
  CheckCircle2,
  Clock,
  DatabaseZap,
  FileSignature,
  LineChart,
  Megaphone,
  MessageCircle,
  PlugZap,
  Star,
  Users,
  type LucideIcon,
} from 'lucide-react';
import { WHATSAPP_LINK } from '../constants';
import useCaseLandingContent from '../content/use-case-landings.json';
import { POSITIONING } from '../content/positioning';
import { MarketingHead, marketingCanonicalUrl } from '../components/marketing/MarketingHead';

type UseCaseKey = 'clinicas' | 'estetica' | 'ventas' | 'integraciones';

type UseCase = {
  key: UseCaseKey;
  path: string;
  eyebrow: string;
  title: string;
  highlight: string;
  description: string;
  seoTitle: string;
  seoDescription: string;
  cta: string;
  whatsappText: string;
  heroImage: string;
  heroImageAlt: string;
  who: string[];
  prioritySteps: Array<{ title: string; description: string }>;
  outcomes: string[];
  modules: Array<{ title: string; description: string; icon: LucideIcon }>;
  integrations: string[];
  integrationGuides?: Array<{ label: string; href: string }>;
  relatedStories: Array<{ title: string; description: string; href: string; tag: string }>;
  featuredProof?: Array<{ title: string; description: string; href: string; tag: string }>;
  processExample?: Array<{ title: string; description: string }>;
  integrationMatrix?: Array<{ software: string; firstUseCase: string; phase: string; href?: string }>;
  proof: string;
  faq: Array<{ q: string; a: string }>;
};

const CONSENTIRME_CASE = {
  title: 'Consentirme',
  description: 'Estética, reservas en Treatwell/Booksy y WhatsApp conectados sin cambiar la operativa del centro.',
  href: '/blog/caso-exito-consentirme-estetica-treatwell-whatsapp.html',
  tag: 'Estética + reservas',
};

const SEVEN_SISTERS_CASE = {
  title: 'Seven Sisters Tattoo',
  description: 'Digitalización de consentimientos, trazabilidad y atención por WhatsApp en un estudio real de Madrid.',
  href: '/blog/caso-exito-seven-sisters-tattoo-madrid.html',
  tag: 'Consentimientos + WhatsApp',
};

export const USE_CASES: Record<UseCaseKey, UseCase> = {
  clinicas: {
    key: 'clinicas',
    ...useCaseLandingContent.clinicas,
    heroImage: '/clinica-agenda-ia.png',
    heroImageAlt: 'Recepción clínica con agenda, WhatsApp y automatización IA conectados',
    who: ['Fisioterapia', 'Psicología', 'Nutrición', 'Clínicas privadas', 'Centros de bienestar', 'Servicios recurrentes'],
    prioritySteps: [
      {
        title: 'Llamadas y dudas repetidas',
        description: 'La IA filtra horarios, precios orientativos, servicios y preguntas frecuentes antes de escalar al equipo.',
      },
      {
        title: 'Recordatorios y no-shows',
        description: 'Confirmaciones, cambios y avisos por WhatsApp para reducir ausencias y huecos improductivos.',
      },
      {
        title: 'Huecos libres y lista de espera',
        description: 'Cuando alguien cancela, el sistema ayuda a priorizar a quién avisar para recuperar agenda.',
      },
    ],
    outcomes: [
      'Mantener atendidas las dudas frecuentes por WhatsApp',
      'Coordinar reservas, cambios y cancelaciones según permisos',
      'Preparar o enviar recordatorios y confirmaciones autorizadas',
      'Mantener visible la lista de espera para aprovechar huecos libres',
      'Escalar al equipo humano cuando haga falta criterio profesional',
    ],
    modules: [
      { title: 'Atención WhatsApp y voz', description: 'La IA responde primeras consultas, precios orientativos, disponibilidad y derivaciones al equipo.', icon: MessageCircle },
      { title: 'Agenda conectada', description: 'Conexión con calendario, CRM o software de reservas para evitar doble trabajo.', icon: CalendarCheck },
      { title: 'Recordatorios y recuperación', description: 'Confirmaciones, cambios, reprogramaciones y avisos para llenar huecos.', icon: Clock },
      { title: 'Panel de seguimiento', description: 'Visibilidad de conversaciones, oportunidades y próximas acciones.', icon: LineChart },
    ],
    integrations: ['Google Calendar', 'TIMP', 'Flowww', 'CRM', 'Gmail', 'Google Sheets', 'WhatsApp Business API'],
    relatedStories: [CONSENTIRME_CASE, SEVEN_SISTERS_CASE],
    featuredProof: [CONSENTIRME_CASE, SEVEN_SISTERS_CASE],
    proof: 'Ideal si ahora dependes de responder manualmente WhatsApp, llamadas o mensajes de Instagram para cerrar reservas.',
  },
  estetica: {
    key: 'estetica',
    ...useCaseLandingContent.estetica,
    heroImage: '/img/consentirme-treatwell-1.jpg',
    heroImageAlt: 'Centro de estética Consentirme, ejemplo real de reservas y WhatsApp conectados',
    who: ['Centros de estética', 'Clínicas estéticas', 'Peluquerías', 'Barberías', 'Uñas y cejas', 'Masajes y bienestar'],
    prioritySteps: [
      {
        title: 'WhatsApp mientras el equipo atiende',
        description: 'Respuestas iniciales, servicios, disponibilidad y derivación a una persona cuando hace falta criterio.',
      },
      {
        title: 'Reservas y recordatorios',
        description: 'Conexión o coordinación con Flowww, Booksy, Treatwell, TIMP o calendario para evitar seguimiento manual.',
      },
      {
        title: 'Reseñas y reactivación',
        description: 'Pedir reseñas tras la visita y preparar campañas aprobadas para clientas inactivas o servicios concretos.',
      },
    ],
    outcomes: [
      'Responder por WhatsApp aunque el equipo esté atendiendo',
      'Coordinar reservas con el software disponible según acceso',
      'Preparar solicitudes y respuestas de reseñas con tono de marca',
      'Preparar campañas aprobadas para clientas inactivas',
      'Crear contenido para Instagram y Google Business con revisión',
    ],
    modules: [
      { title: 'Reservas por WhatsApp', description: 'Primer filtro, disponibilidad, cambios y recordatorios conectados a la operativa.', icon: CalendarCheck },
      { title: 'Marketing IA', description: 'Ideas, posts, campañas y reactivación basadas en servicios, temporada y oportunidades reales.', icon: Megaphone },
      { title: 'Reseñas y reputación', description: 'Solicitud post-visita y respuestas coherentes en Google Business Profile.', icon: Star },
      { title: 'Integraciones de belleza', description: 'Trabajo alrededor de Flowww, Booksy, Treatwell, TIMP, Gmail, Sheets o APIs.', icon: PlugZap },
    ],
    integrations: ['Flowww', 'Booksy', 'Treatwell', 'TIMP', 'Instagram', 'Google Business Profile', 'WhatsApp', 'Google Calendar'],
    relatedStories: [CONSENTIRME_CASE, SEVEN_SISTERS_CASE],
    featuredProof: [CONSENTIRME_CASE, SEVEN_SISTERS_CASE],
    proof: 'Pensada para centros donde cada mensaje no respondido puede acabar siendo una reserva perdida.',
  },
  ventas: {
    key: 'ventas',
    ...useCaseLandingContent.ventas,
    heroImage: '/persona-grupo-reunion.jpg',
    heroImageAlt: 'Equipo revisando oportunidades comerciales y seguimiento de leads',
    who: ['Negocios con leads por WhatsApp', 'Servicios de alto valor', 'Centros con presupuestos', 'Academias', 'Clínicas', 'Equipos comerciales pequeños'],
    prioritySteps: [
      {
        title: 'Captura de oportunidades',
        description: 'Detectar consultas con intención comercial desde WhatsApp, Instagram, formularios o email.',
      },
      {
        title: 'Cualificación y resumen',
        description: 'Extraer servicio, urgencia, presupuesto, objeciones y siguiente pregunta útil sin depender de frases exactas.',
      },
      {
        title: 'Seguimiento aprobado',
        description: 'Preparar la próxima acción y recordatorios para que el equipo decida qué enviar y cuándo.',
      },
    ],
    outcomes: [
      'Detectar interesados desde conversaciones reales',
      'Clasificar servicio, urgencia, presupuesto y objeciones',
      'Crear pipeline con estados y próximas acciones',
      'Preparar respuestas o seguimientos para aprobación',
      'Medir qué canales generan oportunidades reales',
    ],
    modules: [
      { title: 'Captura automática', description: 'Leads desde WhatsApp, Instagram, formularios, email o CRM ligero.', icon: Users },
      { title: 'Cualificación', description: 'Resumen semántico del interés, urgencia, valor potencial y objeciones.', icon: Bot },
      { title: 'Pipeline visible', description: 'Estados, próxima acción y recordatorios para que el seguimiento no dependa de memoria.', icon: LineChart },
      { title: 'Review-only por defecto', description: 'El sistema prepara acciones; los mensajes sensibles salen con aprobación humana.', icon: CheckCircle2 },
    ],
    integrations: ['WhatsApp', 'Instagram', 'Gmail', 'Google Sheets', 'CRM', 'Formularios', 'Google Analytics'],
    relatedStories: [CONSENTIRME_CASE, SEVEN_SISTERS_CASE],
    featuredProof: [CONSENTIRME_CASE, SEVEN_SISTERS_CASE],
    processExample: [
      {
        title: 'Lead entra por WhatsApp o Instagram',
        description: 'La IA identifica si es una consulta real, qué servicio pide y si falta información para avanzar.',
      },
      {
        title: 'Se crea una oportunidad clara',
        description: 'El pipeline muestra fuente, interés, urgencia, objeciones, valor potencial y estado de seguimiento.',
      },
      {
        title: 'El equipo aprueba la respuesta',
        description: 'Ancora propone un mensaje o una tarea, pero los envíos sensibles quedan en modo revisión por defecto.',
      },
      {
        title: 'Nada queda olvidado',
        description: 'Si no hay respuesta, quedan recordatorios y próximas acciones visibles para retomar el contacto.',
      },
    ],
    proof: 'Especialmente útil cuando llegan consultas pero el seguimiento se pierde entre conversaciones, notas y tareas sueltas.',
  },
  integraciones: {
    key: 'integraciones',
    ...useCaseLandingContent.integraciones,
    heroImage: '/equipo-colaborando.jpg',
    heroImageAlt: 'Equipo conectando herramientas, datos e integraciones para automatizar procesos',
    who: ['Negocios con software propio', 'Centros con agenda', 'Equipos con CRM', 'Operativas en Sheets', 'Negocios con varias plataformas', 'Empresas que no quieren migrar'],
    prioritySteps: [
      {
        title: 'Mapa de herramientas actuales',
        description: 'Canales, agenda, CRM, hojas de cálculo, email y puntos donde hoy se copia información a mano.',
      },
      {
        title: 'Primer caso con retorno rápido',
        description: 'Elegimos un flujo acotado: WhatsApp + agenda, lead + CRM, reseña + Google Business o informe en Sheets.',
      },
      {
        title: 'Añadir por fases según impacto',
        description: 'Cuando el primer módulo funciona, conectamos más canales sin obligarte a migrar de plataforma.',
      },
    ],
    outcomes: [
      'Mantener el software actual siempre que sea posible',
      'Conectar WhatsApp con agenda, CRM o base de datos',
      'Evitar copiar datos manualmente entre herramientas',
      'Crear automatizaciones a medida alrededor de la operativa',
      'Medir conversiones, campañas y oportunidades reales',
    ],
    modules: [
      { title: 'Mapeo de operativa', description: 'Analizamos canales, herramientas, datos y puntos donde se pierde tiempo o venta.', icon: DatabaseZap },
      { title: 'Conectores', description: 'APIs, webhooks, automatizaciones, Gmail, Sheets, calendarios y herramientas verticales.', icon: PlugZap },
      { title: 'Capa IA', description: 'Atención, clasificación, resúmenes, recomendaciones y generación de contenido.', icon: Bot },
      { title: 'Control y medición', description: 'Logs, paneles, métricas y alertas para saber qué funciona.', icon: LineChart },
    ],
    integrations: ['Flowww', 'TIMP', 'Booksy', 'Treatwell', 'Google Calendar', 'WhatsApp', 'Instagram', 'Google Business', 'Gmail', 'Sheets', 'Stripe', 'APIs'],
    relatedStories: [CONSENTIRME_CASE, SEVEN_SISTERS_CASE],
    featuredProof: [CONSENTIRME_CASE, SEVEN_SISTERS_CASE],
    integrationMatrix: [
      {
        software: 'Flowww',
        firstUseCase: 'WhatsApp, reservas, campañas y reactivación para estética o clínicas.',
        phase: 'Empezar por atención y recordatorios; después campañas segmentadas.',
        href: '/blog/integracion-flowww-whatsapp-clinicas-estetica.html',
      },
      {
        software: 'Treatwell',
        firstUseCase: 'Reserva, confirmación y seguimiento sin cambiar el escaparate de reservas.',
        phase: 'Primero WhatsApp y caso Consentirme; después reputación y reactivación.',
        href: '/blog/integracion-treatwell-whatsapp-reservas.html',
      },
      {
        software: 'TIMP',
        firstUseCase: 'Agenda, recordatorios y lista de espera para servicios recurrentes.',
        phase: 'Primero no-shows y huecos libres; después informes y campañas.',
        href: '/blog/integracion-timp-whatsapp-reservas.html',
      },
      {
        software: 'Booksy',
        firstUseCase: 'Reservas y seguimiento WhatsApp para belleza, barberías y estética.',
        phase: 'Primero confirmación y cambios; después reseñas y clientes inactivos.',
        href: '/blog/integracion-booksy-whatsapp-reservas.html',
      },
      {
        software: 'Google Calendar / CRM / Sheets',
        firstUseCase: 'CRM ligero, disponibilidad, tareas comerciales y medición básica.',
        phase: 'Ideal para validar rápido antes de una integración más profunda.',
      },
    ],
    proof: 'La ventaja no es tener “otro bot”, sino hacer que las herramientas que ya usas funcionen juntas.',
  },
};

const canonicalUseCasePath = (path: string) => `${path.replace(/\/$/, '')}/`;

export const LANDING_LINKS = Object.values(USE_CASES).map(({ key, path, eyebrow, title, description }) => ({
  key,
  path: canonicalUseCasePath(path),
  eyebrow,
  title,
  description,
}));

export const UseCaseLanding: React.FC<{ useCase: UseCase }> = ({ useCase }) => {
  const whatsappHref = `${WHATSAPP_LINK}?text=${encodeURIComponent(useCase.whatsappText)}`;
  const routeSchema = useMemo(
    () => ({
      '@context': 'https://schema.org',
      '@type': 'Service',
      name: useCase.title,
      description: useCase.seoDescription,
      url: marketingCanonicalUrl(useCase.path),
      provider: {
        '@type': 'Organization',
        name: 'Áncora Automations',
        url: 'https://www.ancoraautomations.es/',
      },
      areaServed: 'ES',
      serviceType: useCase.eyebrow,
    }),
    [useCase],
  );

  return (
    <main className="marketing-shell bg-[#faf8ff] text-slate-900" data-surface={`marketing-${useCase.key}`}>
      <MarketingHead
        title={useCase.seoTitle}
        description={useCase.seoDescription}
        path={useCase.path}
        imagePath={useCase.heroImage}
        imageAlt={useCase.heroImageAlt}
        schema={routeSchema}
      />
      <section className="use-case-hero" aria-labelledby={`use-case-title-${useCase.key}`}>
        <div className="use-case-hero__video" aria-hidden="true">
          <video
            autoPlay
            muted
            loop
            playsInline
            preload="metadata"
            poster="/hero-poster.jpg"
            onLoadedData={(event) => {
              event.currentTarget.play().catch(() => undefined);
            }}
          >
            <source src="/hero-video.mp4" type="video/mp4" />
          </video>
        </div>
        <div className="marketing-grid use-case-hero__grid">
          <div className="use-case-hero__copy">
            <p className="marketing-eyebrow marketing-eyebrow--dark">{useCase.eyebrow}</p>
            <h1 id={`use-case-title-${useCase.key}`}>{useCase.title}</h1>
            <p className="use-case-hero__highlight">{useCase.highlight}</p>
            <p className="use-case-hero__lead">{useCase.description}</p>
            <div className="use-case-hero__actions">
              <a
                href={whatsappHref}
                data-marketing-event="hero_system_cta"
                target="_blank"
                rel="noreferrer"
                className="marketing-button marketing-button--primary"
              >
                <MessageCircle size={20} />
                {useCase.cta}
              </a>
              <a
                href="#como-funciona"
                className="marketing-button marketing-button--secondary"
                data-marketing-event="process_demo"
              >
                Ver módulos
                <ArrowRight size={18} />
              </a>
            </div>
          </div>
          <aside className="use-case-hero__proof" aria-label="Aplicación en una operativa real">
            <div className="use-case-hero__image">
              <img
                src={useCase.heroImage}
                alt={useCase.heroImageAlt}
                loading="eager"
              />
            </div>
            <div className="use-case-hero__proof-copy">
              <p className="use-case-hero__proof-label">
                <Star size={15} />
                Punto de partida real
              </p>
              <p>{useCase.proof}</p>
              <ul aria-label="Negocios a los que se aplica">
                {useCase.who.map((item) => (
                  <li key={item}>{item}</li>
                ))}
              </ul>
            </div>
          </aside>
        </div>
      </section>

      <section className="use-case-process" aria-labelledby={`operational-process-${useCase.key}`}>
        <div className="marketing-grid use-case-process__grid">
          <div className="use-case-process__intro">
            <p className="marketing-eyebrow">Una gestión completa</p>
            <h2 id={`operational-process-${useCase.key}`}>Del mensaje al siguiente paso resuelto</h2>
            <p>
              Áncora conserva el contexto para que el proceso avance y entrega al equipo las decisiones que necesitan criterio.
            </p>
          </div>
          <ol className="use-case-process__rail">
            {POSITIONING.process.map((step, index) => (
              <li key={step.key} style={{ '--step-index': index } as React.CSSProperties}>
                <span className="use-case-process__node">{index + 1}</span>
                <div>
                  <h3>{step.label}</h3>
                  <p>{step.description}</p>
                </div>
              </li>
            ))}
          </ol>
        </div>
      </section>

      <section className="py-16 bg-white border-b border-slate-100">
        <div className="container mx-auto px-4 max-w-6xl">
          <div className="grid lg:grid-cols-[0.9fr_1.1fr] gap-10 items-start">
            <div>
              <span className="text-emerald-700 font-bold uppercase tracking-widest text-sm">Qué automatizamos primero</span>
              <h2 className="text-3xl md:text-5xl font-black tracking-tight mt-3 mb-4">Empezamos por el flujo con más impacto</h2>
              <p className="text-slate-500 text-lg leading-relaxed">
                No hace falta cambiar todo de golpe. Priorizamos por fases según impacto: primero el punto que más llamadas, huecos o leads está perdiendo.
              </p>
            </div>
            <div className="grid gap-4">
              {useCase.prioritySteps.map((step, index) => (
                <div key={step.title} className="rounded-3xl border border-slate-100 bg-slate-50 p-5 shadow-sm">
                  <div className="flex items-start gap-4">
                    <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl bg-emerald-100 text-sm font-black text-emerald-800">
                      {index + 1}
                    </div>
                    <div>
                      <h3 className="text-lg font-black text-slate-900 mb-1">{step.title}</h3>
                      <p className="text-slate-500 text-sm leading-relaxed">{step.description}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {useCase.featuredProof && useCase.featuredProof.length > 0 && (
            <div className="mt-10 rounded-[2rem] border border-emerald-100 bg-emerald-50/60 p-6 md:p-8">
              <div className="flex flex-col gap-6 lg:flex-row lg:items-start lg:justify-between">
                <div className="max-w-md">
                  <span className="text-emerald-700 font-bold uppercase tracking-widest text-xs">Prueba social cerca del problema</span>
                  <h3 className="text-2xl md:text-3xl font-black text-slate-900 mt-2 mb-3">Casos reales antes de decidir</h3>
                  <p className="text-slate-600 leading-relaxed">
                    Si tu centro se parece a alguno de estos escenarios, podemos empezar por una fase pequeña y medir impacto antes de ampliar.
                  </p>
                </div>
                <div className="grid gap-4 md:grid-cols-2 lg:flex-1">
                  {useCase.featuredProof.map((story) => (
                    <a
                      key={story.href}
                      href={story.href}
                      data-marketing-event="case_click"
                      className="group rounded-3xl bg-white border border-emerald-100 p-5 shadow-sm hover:-translate-y-1 hover:shadow-lg transition-all"
                    >
                      <span className="inline-flex rounded-full bg-emerald-100 px-3 py-1 text-xs font-bold uppercase tracking-widest text-emerald-800 mb-3">
                        {story.tag}
                      </span>
                      <h4 className="text-lg font-black text-slate-900 group-hover:text-emerald-700 transition-colors">{story.title}</h4>
                      <p className="text-slate-500 text-sm leading-relaxed mt-2 mb-4">{story.description}</p>
                      <span className="inline-flex items-center gap-2 text-sm font-bold text-emerald-700">
                        Ver caso real
                        <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
                      </span>
                    </a>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      </section>

      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 max-w-6xl">
          <div className="text-center max-w-3xl mx-auto mb-12">
            <span className="text-emerald-700 font-bold uppercase tracking-widest text-sm">Resultado práctico</span>
            <h2 className="text-3xl md:text-5xl font-black tracking-tight mt-3 mb-4">Qué automatizamos en este caso</h2>
            <p className="text-slate-500 text-lg">No vendemos una herramienta genérica: diseñamos el flujo alrededor de cómo trabaja tu negocio.</p>
          </div>
          <div className="grid md:grid-cols-2 gap-4">
            {useCase.outcomes.map((item) => (
              <div key={item} className="flex items-start gap-3 rounded-2xl border border-slate-100 bg-slate-50 p-5">
                <CheckCircle2 className="text-emerald-600 mt-0.5 flex-shrink-0" size={22} />
                <span className="text-slate-700 leading-relaxed">{item}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {useCase.processExample && useCase.processExample.length > 0 && (
        <section className="py-20 bg-slate-950 text-white">
          <div className="container mx-auto px-4 max-w-6xl">
            <div className="grid lg:grid-cols-[0.8fr_1.2fr] gap-10 items-start">
              <div>
                <span className="text-emerald-300 font-bold uppercase tracking-widest text-sm">Ejemplo tangible</span>
                <h2 className="text-3xl md:text-5xl font-black tracking-tight mt-3 mb-4">Así se ve un lead gestionado por el agente</h2>
                <p className="text-slate-300 text-lg leading-relaxed">
                  Cada mensaje se convierte en una oportunidad clara, con contexto, próxima acción y aprobación humana cuando el siguiente paso lo requiere.
                </p>
              </div>
              <div className="grid gap-4">
                {useCase.processExample.map((step, index) => (
                  <div key={step.title} className="rounded-3xl border border-white/10 bg-white/5 p-5 backdrop-blur">
                    <div className="flex items-start gap-4">
                      <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl bg-emerald-400 text-sm font-black text-slate-950">
                        {index + 1}
                      </div>
                      <div>
                        <h3 className="text-lg font-black mb-1">{step.title}</h3>
                        <p className="text-slate-300 text-sm leading-relaxed">{step.description}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>
      )}

      <section id="como-funciona" className="use-case-modules">
        <div className="container mx-auto px-4 max-w-6xl">
          <div className="text-center max-w-3xl mx-auto mb-12">
            <span className="text-emerald-700 font-bold uppercase tracking-widest text-sm">Módulos</span>
            <h2 className="text-3xl md:text-5xl font-black tracking-tight mt-3 mb-4">Empieza por un proceso y amplía según impacto</h2>
            <p className="text-slate-500 text-lg">Atención, integraciones, ventas, voz y marketing se combinan según las necesidades de cada proceso.</p>
          </div>
          <div className="use-case-modules__grid">
            {useCase.modules.map((module) => {
              const Icon = module.icon;
              return (
                <article key={module.title} className="use-case-modules__item">
                  <div className="use-case-modules__icon">
                    <Icon size={24} className="text-emerald-700" />
                  </div>
                  <h3 className="font-black text-lg mb-3">{module.title}</h3>
                  <p className="text-slate-500 text-sm leading-relaxed">{module.description}</p>
                </article>
              );
            })}
          </div>
        </div>
      </section>

      <section className="use-case-integrations">
        <div className="container mx-auto px-4 max-w-6xl">
          <div className="grid lg:grid-cols-[0.85fr_1.15fr] gap-10 items-start">
            <div>
              <span className="text-emerald-700 font-bold uppercase tracking-widest text-sm">Integraciones</span>
              <h2 className="text-3xl md:text-5xl font-black tracking-tight mt-3 mb-4">Conectamos con tu stack actual</h2>
              <p className="text-slate-500 text-lg leading-relaxed mb-6">Si ya tienes una herramienta que funciona, la mantenemos como base para que la IA atienda, clasifique, recomiende y ejecute mejor.</p>
              {useCase.key !== 'integraciones' && (
                <Link
                  to="/integraciones-software-ia/"
                  data-marketing-event="integration_click"
                  className="inline-flex items-center gap-2 font-bold text-emerald-700 hover:text-emerald-800"
                >
                  Ver landing de integraciones
                  <ArrowRight size={18} />
                </Link>
              )}
            </div>
            <div className="use-case-integrations__stack">
              <p className="use-case-integrations__status">Integraciones para tu proceso</p>
              <div className="use-case-integrations__list">
                {useCase.integrations.map((integration) => {
                  const guide = useCase.integrationGuides?.find(({ label }) => label === integration);

                  return guide ? (
                    <a
                      key={integration}
                      href={guide.href}
                      data-marketing-event="integration_click"
                      aria-label={`Ver guía de integración de ${integration}`}
                    >
                      {integration}
                    </a>
                  ) : (
                    <span key={integration}>{integration}</span>
                  );
                })}
              </div>
              <p className="use-case-integrations__disclaimer">
                {POSITIONING.integrationDisclaimer}
              </p>
            </div>
          </div>

          {useCase.integrationMatrix && useCase.integrationMatrix.length > 0 && (
            <div className="mt-12 rounded-[2rem] border border-slate-100 bg-slate-50 p-5 md:p-8">
              <div className="mb-6 max-w-3xl">
                <span className="text-emerald-700 font-bold uppercase tracking-widest text-xs">Matriz de primer caso de uso</span>
                <h3 className="text-2xl md:text-3xl font-black text-slate-900 mt-2 mb-3">Si usas esta herramienta, empezamos por esto</h3>
                <p className="text-slate-500 leading-relaxed">
                  Definimos un primer caso de uso, una fase recomendada y, cuando existe, un recurso específico para profundizar.
                </p>
              </div>
              <div className="grid gap-4">
                {useCase.integrationMatrix.map((row) => {
                  const content = (
                    <div className="grid gap-3 md:grid-cols-[0.75fr_1.2fr_1.2fr] md:items-start">
                      <h4 className="font-black text-slate-900">{row.software}</h4>
                      <p className="text-sm text-slate-600 leading-relaxed">{row.firstUseCase}</p>
                      <p className="text-sm text-slate-500 leading-relaxed">{row.phase}</p>
                    </div>
                  );

                  return row.href ? (
                    <a
                      key={row.software}
                      href={row.href}
                      data-marketing-event="integration_click"
                      className="group block rounded-3xl bg-white border border-slate-100 p-5 shadow-sm hover:-translate-y-1 hover:shadow-lg transition-all"
                    >
                      {content}
                      <span className="mt-3 inline-flex items-center gap-2 text-sm font-bold text-emerald-700">
                        Ver recurso específico
                        <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
                      </span>
                    </a>
                  ) : (
                    <div key={row.software} className="rounded-3xl bg-white border border-slate-100 p-5 shadow-sm">
                      {content}
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </section>

      {(!useCase.featuredProof || useCase.featuredProof.length === 0) && (
        <section className="py-20 bg-slate-50">
          <div className="container mx-auto px-4 max-w-6xl">
            <div className="grid lg:grid-cols-[0.75fr_1.25fr] gap-10 items-start">
              <div>
                <span className="text-emerald-700 font-bold uppercase tracking-widest text-sm">Casos relacionados</span>
                <h2 className="text-3xl md:text-5xl font-black tracking-tight mt-3 mb-4">Ejemplos reales conectados con esta solución</h2>
                <p className="text-slate-500 text-lg leading-relaxed">
                  Antes de montar módulos nuevos, mira cómo aplicamos la misma lógica en negocios reales: conectar canales, software y procesos para reducir trabajo manual y no perder oportunidades.
                </p>
              </div>
              <div className="grid md:grid-cols-2 gap-5">
                {useCase.relatedStories.map((story) => (
                  <a
                    key={story.href}
                    href={story.href}
                    data-marketing-event="case_click"
                    className="group rounded-3xl bg-white border border-slate-100 p-6 shadow-sm hover:-translate-y-1 hover:shadow-xl transition-all"
                  >
                    <span className="inline-flex rounded-full bg-emerald-50 px-3 py-1 text-xs font-bold uppercase tracking-widest text-emerald-700 mb-4">
                      {story.tag}
                    </span>
                    <h3 className="text-xl font-black text-slate-900 mb-3 group-hover:text-emerald-700 transition-colors">{story.title}</h3>
                    <p className="text-slate-500 text-sm leading-relaxed mb-5">{story.description}</p>
                    <span className="inline-flex items-center gap-2 text-sm font-bold text-emerald-700">
                      Ver caso
                      <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
                    </span>
                  </a>
                ))}
              </div>
            </div>
          </div>
        </section>
      )}

      <section className="py-20 bg-slate-950 text-white">
        <div className="container mx-auto px-4 max-w-5xl">
          <div className="text-center mb-10">
            <span className="text-emerald-300 font-bold uppercase tracking-widest text-sm">Preguntas habituales</span>
            <h2 className="text-3xl md:text-5xl font-black tracking-tight mt-3">Resolvemos tus dudas</h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4 mb-12">
            {useCase.faq.map((item) => (
              <div key={item.q} className="rounded-3xl border border-white/10 bg-white/5 p-6">
                <h3 className="font-black mb-3">{item.q}</h3>
                <p className="text-slate-300 text-sm leading-relaxed">{item.a}</p>
              </div>
            ))}
          </div>
          <div className="rounded-3xl bg-gradient-to-br from-emerald-500 to-cyan-400 p-8 md:p-10 text-slate-950 text-center">
            <FileSignature size={34} className="mx-auto mb-4" />
            <h2 className="text-3xl md:text-4xl font-black mb-4">¿Quieres verlo aplicado a tu negocio?</h2>
            <p className="text-slate-900/80 max-w-2xl mx-auto mb-8">Dinos qué canales usas, qué software tienes y dónde se pierden más oportunidades. Te diremos qué automatizar primero y qué dejar para más adelante.</p>
            <a
              href={whatsappHref}
              data-marketing-event="pricing_cta"
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center justify-center gap-2 rounded-full bg-slate-950 px-8 py-4 font-extrabold text-white shadow-xl hover:-translate-y-0.5 transition-transform"
            >
              <MessageCircle size={20} />
              {useCase.cta}
            </a>
          </div>
        </div>
      </section>
    </main>
  );
};
